<template>
    <QLayout view="lHh LpR fFf" class="Project">
        <Sidebar
            v-model="tab"
            :tabs="tabs"
            @update:modelValue="handleChangeTab"
        />

        <QDrawer
            v-if="hasContextDrawer"
            v-model="rightDrawerOpen"
            side="right"
            :overlay="isMobile"
            :width="320"
            class="ContextDrawer q-px-sm"
        >
            <div
                class="ContextDrawerHeader"
                :class="{ 'ContextDrawerHeader--detail': isDetailDrawer }"
            >
                <div
                    v-if="isDetailDrawer"
                    class="ContextDrawerHeaderRow ContextDrawerHeaderRow--detail"
                >
                    <q-btn
                        flat
                        round
                        dense
                        color="primary"
                        icon="chevron_left"
                        :aria-label="$t('general.back')"
                        @click="handleDetailBack"
                    />

                    <div class="ContextDrawerHeaderCenter">
                        <q-icon
                            :name="drawerIcon"
                            color="primary"
                            size="20px"
                            class="q-mr-sm"
                        />
                        <div class="ContextDrawerTitle text-primary">
                            {{ drawerTitle }}
                        </div>
                    </div>

                    <div class="ContextDrawerHeaderRight" />
                </div>

                <div
                    v-else
                    class="flex items-center no-wrap"
                >
                    <q-icon
                        :name="drawerIcon"
                        color="primary"
                        size="20px"
                        class="q-mr-sm"
                    />
                    <div class="ContextDrawerTitle text-primary">
                        {{ drawerTitle }}
                    </div>
                </div>
            </div>

            <q-separator color="primary" />

            <div class="q-pa-md">
                <Transition
                    name="drawer-panel"
                    mode="out-in"
                >
                    <component
                        :is="contextDrawerPanel.component"
                        :key="contextDrawerPanelKey"
                        v-bind="contextDrawerPanel.props"
                        v-on="contextDrawerPanel.on"
                    />
                </Transition>
            </div>
        </QDrawer>
        <QHeader elevated class="Header">
            <AdminHeader>
                <template #actions>
                    <div
                        v-if="isWizardRoute"
                        class="flex items-center"
                    >
                        <QBtn
                            v-if="wizardConfig?.exit"
                            flat
                            noCaps
                            color="primary"
                            class="q-ml-sm"
                            @click="handleWizardClickExit"
                        >
                            {{ $t('wizard.cancel') }}
                        </QBtn>

                        <QBtn
                            v-if="wizardStep?.next && !wizardStep?.end"
                            outline
                            noCaps
                            color="primary"
                            class="q-ml-sm"
                            :disable="wizardConfig?.isNavigate"
                            @click="wizardConfig?.nextStep?.()"
                        >
                            {{ $t('wizard.continue') }}
                        </QBtn>
                        <QBtn
                            v-if="wizardStep?.end"
                            noCaps
                            color="primary"
                            class="q-ml-sm"
                            outline
                            :disable="wizardConfig?.isNavigate"
                            @click="wizardConfig?.nextStep?.()"
                        >
                            {{ $t('wizard.finish') }}
                        </QBtn>
                    </div>

                    <div
                        v-else-if="hasContextDrawer"
                        class="flex row-stretch-center"
                    >
                        <QBtn
                            flat
                            round
                            dense
                            color="primary"
                            icon="view_sidebar"
                            :aria-label="$t('general.filters')"
                            @click="rightDrawerOpen = !rightDrawerOpen"
                        />
                    </div>
                    <HeaderList
                        v-else
                        v-model="filters"
                        :disabled="isProjectArchived"
                        @update:modelValue="handleChangeQuery"
                    />
                </template>
            </AdminHeader>
        </QHeader>
        <QPageContainer class="Container">
            <QPage
                class="Screen flex-1"
                :class="{ isDesktop: !isMobile }"
            >
                <RouterView v-slot="{ Component, props }">
                    <Transition
                        v-if="projectId"
                        name="main-router"
                        mode="out-in"
                    >
                        <component
                            :is="Component"
                            ref="activeView"
                            v-bind="props"
                            :projectId="projectId"
                            :query="query"
                            :filters="filters"
                            :sortBy="currentSortBy"
                            :sortDirection="currentSortDirection"
                            :isProjectArchived="isProjectArchived"
                            @open-dataset-info="handleOpenDatasetInfo"
                            @open-model-info="handleOpenModelInfo"
                            @wizard-header-change="handleWizardHeaderChange"
                        />
                    </Transition>
                </RouterView>
            </QPage>
        </QPageContainer>

        <ModalRequest
            ref="delPrDialog"
            :title="$t('projects.delete')"
            :text="$t('projects.modals.delete.text', { name: (project || {}).name })"
            :loading="isLoading"
            @on-accept="archiveProject"
        />
        <ModalNameDesc
            ref="editPrDialog"
            :action="$t('actions.save')"
            :title="$t('projects.edit')"
            :loading="isLoading"
            @on-submit="updateProject"
        />
    </QLayout>
</template>

<script>
import {
    Api, apiRequest, notifyError, notifySuccess,
} from '@/api'
import { mediaQueryMixin } from '@/mixins'
import {
    AdminHeader, ModalRequest, ModalNameDesc, HeaderList, Sidebar,
} from '@/components'

import {
    ProjectDatasetsDrawerPanel,
    ProjectDatasetInfoDrawerPanel,
    ProjectModelInfoDrawerPanel,
    ProjectModelsDrawerPanel,
    getProjectContextDrawerPanel,
} from '@/screens/Project/contextDrawerPanels'

const FILTERS = {
    search: '',
    archived: false,
}

export default {
    name: 'ProjectComponent',
    components: {
        AdminHeader,
        ModalRequest,
        ModalNameDesc,
        HeaderList,
        Sidebar,
        ProjectDatasetsDrawerPanel,
        ProjectDatasetInfoDrawerPanel,
        ProjectModelInfoDrawerPanel,
        ProjectModelsDrawerPanel,
    },
    mixins: [mediaQueryMixin],
    inject: ['$DEV'],
    data() {
        return {
            tab: '',
            projectId: undefined,
            project: undefined,
            isLoading: false,

            rightDrawerOpen: true,
            drawerSearch: '',
            datasetsSortBy: 'CREATED_AT',
            datasetsSortDirection: 'DESCENDING',
            datasetDrawerDataset: null,
            modelDrawerModel: null,
            modelsSortBy: 'NAME',
            modelsSortDirection: 'ASCENDING',

            query: undefined,

            filters: { ...FILTERS },

            wizardHeaderState: {
                config: null,
                step: null,
            },
        }
    },
    computed: {
        routeName() {
            return this.$route?.name
        },
        isWizardRoute() {
            return typeof this.routeName === 'string' && this.routeName.startsWith('Wizard')
        },
        wizardConfig() {
            return this.wizardHeaderState?.config || null
        },
        wizardStep() {
            return this.wizardHeaderState?.step || null
        },
        contextDrawerPanel() {
            return getProjectContextDrawerPanel(this)
        },
        contextDrawerPanelKey() {
            const base = this.contextDrawerPanel?.component || 'none'
            if (this.isDatasetInfoDrawer) return `${base}-${this.datasetDrawerDataset?.uuid || this.datasetDrawerDataset?.id || 'detail'}`
            if (this.isModelInfoDrawer) return `${base}-${this.modelDrawerModel?.uuid || this.modelDrawerModel?.id || 'detail'}`
            return `${base}-list`
        },
        hasContextDrawer() {
            return !!this.contextDrawerPanel?.component
        },
        isDatasetInfoDrawer() {
            return this.routeName === 'ProjectDatasets' && !!this.datasetDrawerDataset
        },
        isModelInfoDrawer() {
            return this.routeName === 'ProjectModels' && !!this.modelDrawerModel
        },
        isDetailDrawer() {
            return this.isDatasetInfoDrawer || this.isModelInfoDrawer
        },
        drawerTitle() {
            if (this.isWizardRoute) return this.wizardStep?.title || ''
            if (this.isDatasetInfoDrawer) return this.datasetDrawerDataset?.name || ''
            if (this.isModelInfoDrawer) return this.modelDrawerModel?.modelName || ''
            return this.selected?.label || ''
        },
        drawerIcon() {
            if (this.isWizardRoute) return 'tune'
            if (this.isDatasetInfoDrawer) return 'info'
            if (this.isModelInfoDrawer) return 'info'
            return this.selected?.icon || 'tune'
        },
        selected() {
            return this.tabs.find(({ id }) => id === this.tab) || {}
        },
        datasetsSortOptions() {
            return [
                { label: this.$t('general.createdAt'), value: 'CREATED_AT' },
                { label: this.$t('general.name'), value: 'NAME' },
            ]
        },
        modelsSortOptions() {
            return [
                { label: this.$t('general.name'), value: 'NAME' },
            ]
        },
        sortDirectionOptions() {
            return [
                { label: this.$t('general.ascending'), value: 'ASCENDING' },
                { label: this.$t('general.descending'), value: 'DESCENDING' },
            ]
        },
        currentSortBy() {
            if (this.routeName === 'ProjectDatasets') return this.datasetsSortBy
            if (this.routeName === 'ProjectModels') return this.modelsSortBy
            return undefined
        },
        currentSortDirection() {
            if (this.routeName === 'ProjectDatasets') return this.datasetsSortDirection
            if (this.routeName === 'ProjectModels') return this.modelsSortDirection
            return undefined
        },
        tabs() {
            return [
                {
                    id: 'ProjectDatasets',
                    icon: 'sym_o_graph_1',
                    label: this.$t('projects.sections.datasets'),
                },
                {
                    id: 'ProjectModels',
                    icon: 'schema',
                    label: this.$t('projects.sections.models'),
                },
            ]
        },
        isProjectArchived() {
            const { deleteDate } = this.project || {}
            return !!deleteDate
        },
    },
    watch: {
        '$route.name': {
            handler(newRouteName, oldRouteName) {
                this.filters = { ...FILTERS }
                this.tab = this.getTabName()

                if (this.isWizardRoute) {
                    this.datasetDrawerDataset = null
                    this.modelDrawerModel = null
                    this.rightDrawerOpen = false
                }

                if (
                    typeof oldRouteName === 'string'
                    && oldRouteName.startsWith('Wizard')
                    && newRouteName === 'ProjectDatasets'
                    && !this.isMobile
                ) {
                    this.rightDrawerOpen = true
                }

                if (!this.hasContextDrawer) return

                // Reset vista detalle al cambiar de sección
                if (this.routeName !== 'ProjectDatasets') this.datasetDrawerDataset = null
                if (this.routeName !== 'ProjectModels') this.modelDrawerModel = null

                // Mantener drawer sincronizado con filtros
                this.drawerSearch = this.filters.search || ''

                if (this.routeName === 'ProjectDatasets') this.syncDatasetsSortFromStorage()
                if (this.routeName === 'ProjectModels') this.syncModelsSortFromStorage()
            },
        },
    },
    mounted() {
        const { params, query = {} } = this.$route
        const { projectId } = params

        this.projectId = projectId
        this.filters = {
            archived: query.archived === 'true',
            search: query.search || '',
        }
        this.drawerSearch = this.filters.search || ''
        this.query = {
            archived: query.archived === 'true',
            search: query.search || '',
        }

        this.tab = this.getTabName()
        this.getProject()

        if (this.isMobile) this.rightDrawerOpen = false
    },
    methods: {
        handleWizardHeaderChange({ config, step } = {}) {
            this.wizardHeaderState = {
                config: config || null,
                step: step || null,
            }
        },
        handleWizardClickExit() {
            this.$refs.activeView?.handleClickExit?.()
        },
        handleOpenDatasetInfo(dataset) {
            if (this.routeName !== 'ProjectDatasets') return
            this.datasetDrawerDataset = dataset
            this.rightDrawerOpen = true
        },
        handleOpenModelInfo(model) {
            if (this.routeName !== 'ProjectModels') return
            this.modelDrawerModel = model
            this.rightDrawerOpen = true
        },
        handleDetailBack() {
            if (this.isDatasetInfoDrawer) this.datasetDrawerDataset = null
            if (this.isModelInfoDrawer) this.modelDrawerModel = null
        },
        handleModelPreview() {
            if (!this.modelDrawerModel) return
            this.$refs.activeView?.handleViewModel?.(this.modelDrawerModel)
        },
        handleDrawerSearchUpdate(value) {
            this.drawerSearch = value
        },
        handleDatasetsSortByUpdate(value) {
            this.datasetsSortBy = value
        },
        handleDatasetsSortDirectionUpdate(value) {
            this.datasetsSortDirection = value
        },
        handleModelsSortByUpdate(value) {
            this.modelsSortBy = value
        },
        handleModelsSortDirectionUpdate(value) {
            this.modelsSortDirection = value
        },
        parseStoredValue(raw) {
            if (raw === null || raw === undefined) return undefined
            try {
                return JSON.parse(raw)
            } catch (e) {
                return raw
            }
        },
        syncDatasetsSortFromStorage() {
            try {
                const rawSortBy = localStorage.getItem('datasetsListSortBy')
                const sortBy = this.parseStoredValue(rawSortBy)
                if (sortBy) this.datasetsSortBy = sortBy
            } catch (e) { /* ignore */ }

            try {
                const rawDirection = localStorage.getItem('datasetsListSortDirection')
                const direction = this.parseStoredValue(rawDirection)
                if (direction) this.datasetsSortDirection = direction
            } catch (e) { /* ignore */ }
        },
        syncModelsSortFromStorage() {
            try {
                const rawSortBy = localStorage.getItem('modelsListSortBy')
                const sortBy = this.parseStoredValue(rawSortBy)
                if (sortBy) this.modelsSortBy = sortBy
            } catch (e) { /* ignore */ }

            try {
                const rawDirection = localStorage.getItem('modelsListSortDirection')
                const direction = this.parseStoredValue(rawDirection)
                if (direction) this.modelsSortDirection = direction
            } catch (e) { /* ignore */ }
        },
        handleCreate() {
            if (this.routeName === 'ProjectDatasets') this.$refs.activeView?.handleCreateItem?.()
            if (this.routeName === 'ProjectModels') this.$refs.activeView?.handleUploadItem?.()
        },
        applySearch() {
            this.filters = { ...this.filters, search: this.drawerSearch }
            this.handleChangeQuery(this.filters)
        },
        applyDatasetsSort() {
            try { localStorage.setItem('datasetsListSortBy', this.datasetsSortBy) } catch (e) { /* ignore */ }
            try { localStorage.setItem('datasetsListSortDirection', this.datasetsSortDirection) } catch (e) { /* ignore */ }
            this.$refs.activeView?.fetchData?.()
        },
        applyModelsSort() {
            try { localStorage.setItem('modelsListSortBy', this.modelsSortBy) } catch (e) { /* ignore */ }
            try { localStorage.setItem('modelsListSortDirection', this.modelsSortDirection) } catch (e) { /* ignore */ }
            this.$refs.activeView?.fetchData?.()
        },
        handleChangeTab(name) {
            const tab = this.tabs.find(({ id }) => id === name)
            if (tab && !tab.disabled) {
                this.query = undefined
                this.$router.push({ name, params: { projectId: this.projectId } }).catch(() => {})
            }
        },
        handleChangeQuery({ archived, search } = {}) {
            if (!archived && !search) {
                this.$router.replace(this.$router).catch(() => {})
            } else {
                const query = {
                    ...(archived ? { archived } : {}),
                    ...(search ? { search } : {}),
                }
                this.$router.replace({ ...this.$router, query }).catch(() => {})
            }
        },
        handleConfirmDelete() {
            this.$refs.delPrDialog.open()
        },
        handleEditProject() {
            const { name, description } = this.project
            this.$refs.editPrDialog.open({ name, description })
        },
        async getProject() {
            this.isLoading = true
            apiRequest(Api().projects.get({ projectId: this.projectId }))
                .then(({ data }) => {
                    const [project] = data
                    this.project = project
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        async updateProject({ name, description }) {
            this.isLoading = true
            apiRequest(Api().projects.update({ projectId: this.projectId, params: { name, description } }))
                .then(() => {
                    notifySuccess(this.$t('projects.notifications.deleted.text'))
                    this.getProject()
                    this.$refs.editPrDialog.close()
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        async archiveProject() {
            this.isLoading = true
            apiRequest(Api().projects.delete({ projectId: this.projectId }))
                .then(() => {
                    notifySuccess(this.$t('projects.notifications.archived.text'))
                    this.getProject()
                    this.$refs.delPrDialog.close()
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        getTabName() {
            if (this.isWizardRoute) return 'ProjectDatasets'
            return this.$route.name
        },
    },
}
</script>

<style lang="scss">
.ContextDrawer {
    background: $gray-bg;

    .q-field--outlined .q-field__control {
        background: $gray-bg;
    }

    .q-field--outlined:not(.q-field--focused) .q-field__control:before {
        border-color: $gray-04;
        background: $gray-controls;
    }

    .q-btn {
        font-weight: 500;
    }

    .q-btn--outline:before,
    .q-btn-group--outline:before {
        border-width: 2px;
    }
}

.ContextDrawerHeader {
    padding: 0 16px;
    height: $header-height;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ContextDrawerHeader--detail {
    justify-content: flex-start;
}

.ContextDrawerHeaderRow--detail {
    width: 100%;
    display: grid;
    grid-template-columns: 32px 1fr 32px;
    align-items: center;
}

.ContextDrawerHeaderCenter {
    justify-self: center;
    display: flex;
    align-items: center;
    min-width: 0;
}

.ContextDrawerHeaderRight {
    width: 32px;
    height: 32px;
}

.ContextDrawerTitle {
    font-weight: 500;
    letter-spacing: 1.25px;
}

.drawer-panel-enter-active,
.drawer-panel-leave-active {
    transition: opacity 160ms ease, transform 160ms ease;
}

.drawer-panel-enter-from,
.drawer-panel-leave-to {
    opacity: 0;
    transform: translateX(8px);
}
</style>

<style scoped lang="scss">
.Header {
    background: $white;
    height: $header-height;
}

.Screen {
    padding: 25px;
    height: 100%;
    overflow: auto;
    width: 100%;

    background: $gray-bg;
}

</style>
